import React, { useMemo, useState, useEffect } from "react";
import styles from "./Dashboard.module.scss";
import {
  FiMonitor,
  FiClock,
  FiMail,
  FiMessageCircle,
  FiSearch,
  FiGrid,
  FiCalendar,
  FiSend,
  FiArchive,
  FiDownload,
  FiRotateCcw
} from "react-icons/fi";
import Header from "../Header/header";
import QrModal from "../Dashboard/QrModal";
import axios from "axios";
import TutorialOverlay from "./TutorialOverlay";  
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import { Modal, Box, Button } from "@mui/material";
import { toast } from "react-toastify";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import Lottie from "lottie-react";
import bulkDownloadAnim from "./animations/bulkDownloading.json";
import { FiEye } from "react-icons/fi";
import { FiAlertTriangle } from "react-icons/fi";
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';

// ---------- Types ----------
type ConsentStatus = "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
type TabKey = "today" | "reminders" | "upload";
type Appointment = {
  id: string;
  time: string;
  date?: string;
  patient: string;
  clinician: string;
  consentStatus: "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
  emails: number;
  texts: number;
  reminders?: number;
  lastReminder?: string | null;
  daysUntilAppointment?: number; 
} & Record<string, any>;

type UploadRow = {
  id: string;
  patient: string;
  appointmentDate: string; 
  clinician: string;
  consentState: "SIGNED" | "PENDING";
  status: string; 
  email: number;
  sms: number;
  signedDate: string | null; 
  raw: Record<string, any>;
  isArchived?: boolean;
};

// ---------- Data (keep for other tabs only) ----------
const REMINDER_APPTS: Appointment[] = [
];

const UPLOAD_ROWS: UploadRow[] = [
  { id: "u1", patient: "Michael Chen", appointmentDate: "2025-01-10", clinician: "Dr Brown", consentState: "SIGNED",  status: "COMPLETED",     email: 1, sms: 1, signedDate: "2025-12-21", raw: {} },
  { id: "u2", patient: "Robert Davis",  appointmentDate: "2025-01-12", clinician: "Dr Brown", consentState: "SIGNED",  status: "COMPLETED",     email: 1, sms: 1, signedDate: "2025-12-17", raw: {} },
  { id: "u3", patient: "Robert Davis",  appointmentDate: "2025-01-18", clinician: "Dr Brown", consentState: "PENDING", status: "SENT_UNSIGNED", email: 1, sms: 1, signedDate: null,        raw: {} },
  { id: "u4", patient: "Michael Chen",  appointmentDate: "2025-01-20", clinician: "Dr Brown", consentState: "PENDING", status: "SENT_UNSIGNED", email: 1, sms: 1, signedDate: null,        raw: {} },
];

// ---------- Helpers ----------
const fmtLong = new Intl.DateTimeFormat("en-GB", {
  weekday: "long", month: "long", day: "numeric", year: "numeric",
}).format;

const fmtShort = (d: Date) =>
  new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);

const daysUntil = (iso: string) => {
  const today = new Date();
  const target = new Date(iso + "T00:00:00");
  const ms = target.getTime() - new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const todayLong = fmtLong(new Date());

// "2025-09-22 12:05" -> "12:05"
const hhmm = (dt?: string | null) => {
  if (!dt) return "00:00";
  const fixed = dt.includes("T") ? dt : dt.replace(" ", "T");
  const d = new Date(fixed);
  if (isNaN(d.getTime())) return "00:00";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

const onlyDate = (dt?: string | null) => {
  if (!dt) return undefined;
  const t = dt.includes("T") ? dt.split("T")[0] : dt.split(" ")[0];
  return t || undefined;
};

const getUploadStatusBadgeClass = (statusText: string, uploaded: boolean) => {
  const s = (statusText || "").toLowerCase().trim();
  if (s.includes("archived")) return "badgeGray";
  if (uploaded) return "badgeSuccessSoft";
  if (s.includes("draft")) return "badgeGray";
  if (s.includes("sign")) return "badgeYellow";
  if (s.includes("completed")) return "badgeSuccessSoft";
  if (s.includes("unsigned") || s.includes("pending")) return "badgeWarn";
  if (s.includes("expired")) return "badgeBlack"; // if you have it
  return "badgeGray";
};


// map for waiting-room + future-appointments
const toAppointment = (raw: any, idx: number): Appointment => {
  const id = String(raw.id ?? raw.appointment_id ?? raw.uuid ?? `wr-${Date.now()}-${idx}`);
  const time = raw.appointment_time ?? hhmm(raw.next_appointment_date) ?? "00:00";

  const patient =
    raw.patient?.first_name && raw.patient?.last_name
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : raw.patient_name ?? raw.patient ?? "Unknown Patient";

  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";

  // Detect archive flags coming from backend
  const isArchived =
    raw?.is_archived === true ||
    raw?.archived === true ||
    !!raw?.archived_at;

  // Build a human status used by UI badge + filter dropdown
  const rawStatus = String(
    raw?.status ??
      raw?.consent_status ??
      raw?.consentStatus ??
      raw?.consent_status_label ??
      ""
  ).trim();

  const status = isArchived ? "Archived" : (rawStatus || "Draft");

  const consentRaw = String(raw.consent_status ?? raw.status ?? "").toUpperCase();
  let consentStatus: ConsentStatus = "NOT_SIGNED";
  if (consentRaw.includes("BOUNCE")) consentStatus = "EMAIL_BOUNCED";
  else if (consentRaw.includes("SIGNED")) consentStatus = "SIGNED";

  const emails = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const texts = Number(raw.sms_sent_count ?? raw.sms_count ?? 0);

  const date = raw.next_appointment_date
    ? raw.next_appointment_date.split("T")[0] || raw.next_appointment_date.split(" ")[0]
    : undefined;

  const reminders = Number(raw.no_of_reminders_sent ?? 0);

  const lastReminder = raw.last_reminder_sent_at
    ? raw.last_reminder_sent_at.split("T")[0] || raw.last_reminder_sent_at.split(" ")[0]
    : null;

  const daysUntilAppointment =
    typeof raw.days_until_appointment === "number" ? raw.days_until_appointment : undefined;

  const dob =
    raw.patient?.date_of_birth ??
    raw.date_of_birth ??
    raw.patient_date_of_birth ??
    null;

  return {
    ...raw,
    raw,               
    id,
    time,
    date,
    patient,
    clinician,
    status,            
    isArchived,        
    consentStatus,
    emails,
    texts,
    reminders,
    lastReminder,
    daysUntilAppointment,
    date_of_birth: dob,
    patient_email: raw.patient?.email ?? raw.patient_email ?? "",
  };
};



const toUploadRow = (raw: any, idx: number): UploadRow => {
  const id = String(raw.id ?? raw.treatment_plan_id ?? raw.uuid ?? `up-${Date.now()}-${idx}`);
  const patient =
    (raw?.patient?.first_name && raw?.patient?.last_name)
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : raw.patient_name ?? raw.patient ?? "Unknown Patient";

  let appointmentDate: string | undefined;
  if (raw.treatment_appointment_dates) {
    try {
      const arr = JSON.parse(String(raw.treatment_appointment_dates));
      if (Array.isArray(arr) && arr.length > 0) {
        appointmentDate = onlyDate(arr[0]);
      }
    } catch {
      appointmentDate = onlyDate(String(raw.treatment_appointment_dates));
    }
  }
  if (!appointmentDate) appointmentDate = onlyDate(raw.next_appointment_date) ?? "1970-01-01";

  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";
  const signedDate = raw.signed_date ? onlyDate(raw.signed_date) ?? null : null;
  const consentState: UploadRow["consentState"] = signedDate ? "SIGNED" : "PENDING";

 const isArchived =
    raw?.is_archived === true ||
    raw?.archived === true ||
    !!raw?.archived_at;

  // status text (keep raw status but show Archived if archived)
  const baseStatus = String(raw.status ?? (raw.uploaded_to_dentally_patient_record ? "Completed" : "Pending"));

  const status: string = String(raw.status ?? (raw.uploaded_to_dentally_patient_record ? "Completed" : "Pending"));

  const email = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const sms   = Number(raw.sms_sent_count   ?? raw.sms_count    ?? 0);

  return {
    id,
    patient,
    appointmentDate,
    clinician,
    consentState,
    status,
    email,
    sms,
    signedDate,
    isArchived,
    raw: { ...raw },
  };
};

// ---------- Component ----------
const Dashboard: React.FC = () => {
  const [clinician, setClinician] = useState("all"); 
  const [consent, setConsent] = useState("all");
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [showTutorial, setShowTutorial] = useState(true);
  const steps = [
    { selector: ".tutorial-filters", text: "Filter by clinician and patient search here." },
    { selector: ".tutorial-tabs", text: "Switch tabs between Waiting Room, Reminders, and Upload." },
    { selector: ".tutorial-table", text: "Here’s where you see appointments and actions." },
    { selector: ".tutorial-qr", text: "Click here to open the QR Code for this appointment." }
  ];
  // QR modal state
  const [qrOpen, setQrOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState<any>(null);
  // Archive confirmation state
  const [confirmArchive, setConfirmArchive] = useState<{
    open: boolean;
    id?: string | number;
    name?: string;
    source: "upload" | "reminders" | null;
    loading: boolean;
  }>({ open: false, source: null, loading: false });
  // selections per tab
  const [reminderSelected, setReminderSelected] = useState<Set<string>>(new Set());
  const [uploadSelected, setUploadSelected] = useState<Set<string>>(new Set());
  // Today
  const [todayAppts, setTodayAppts] = useState<Appointment[]>([]);
  const [loadingToday, setLoadingToday] = useState<boolean>(false);
  const [errorToday, setErrorToday] = useState<string | null>(null);
  // Reminders
  const [reminderAppts, setReminderAppts] = useState<Appointment[]>([]);
  const [loadingRem, setLoadingRem] = useState<boolean>(false);
  const [errorRem, setErrorRem] = useState<string | null>(null);
  const [totalRem, setTotalRem] = useState<number>(0);
  // Upload
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([]);
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);
  const [totalUpload, setTotalUpload] = useState<number>(0);
  // shared pagination
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);
  // upload tab pagination
  const [pageUpload, setPageUpload] = useState<number>(1);
  const [perPageUpload, setPerPageUpload] = useState<number>(25);
  // user + clinicians
  const [user, setUser] = React.useState<any>(null);
  const [clinicians, setClinicians] = React.useState<Array<{ value: string; label: string }>>([]);
  const consentParam = (c: string | undefined) => {
    if (c === "signed") return "SIGNED";
    if (c === "not_signed") return "NOT_SIGNED";
    if (c === "email_bounced") return "EMAIL_BOUNCED";
    return undefined;
  };

  // All tab
  const [allRows, setAllRows] = useState<any[]>([]);
  const [loadingAll, setLoadingAll] = useState<boolean>(false);
  const [errorAll, setErrorAll] = useState<string | null>(null);
  const [totalAll, setTotalAll] = useState<number>(0);
  const [pageAll, setPageAll] = useState<number>(1);
  const [perPageAll, setPerPageAll] = useState<number>(25);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReminderIds, setPendingReminderIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [consentStatusParam, setConsentStatusParam] = useState<string | undefined>(undefined);
  const [autoClearFilters, setAutoClearFilters] = useState(true);

  const [patientQuery, setPatientQuery] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [selectedAllTab, setSelectedAllTab] = useState(new Set());
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");
  const [interactionLog, setInteractionLog] = useState({});
  const [quickViewId, setQuickViewId] = useState(null);


  useEffect(() => {
  const fetchData = async () => {
    setLoadingToday(true);
    setErrorToday(null);

    try {
      const params: Record<string, any> = {
        page,
        per_page: perPage,
      };

      if (clinician !== "all") params.clinician = clinician;

      const cs = consentParam(consent);
      if (cs) params.consent_status = cs;

      if (selectedPatientId) params.patient_id = selectedPatientId;
      if (consentStatusParam) params.consent_status = consentStatusParam;

      const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/waiting-room`;

      const resp = await axios.get(endpoint, {
        params,
        headers: { Accept: "application/json" },
      });

      const payload = Array.isArray(resp.data?.data)
        ? resp.data.data
        : Array.isArray(resp.data)
        ? resp.data
        : [];

      const normalized = (payload as any[]).map((r, i) => toAppointment(r, i));
      const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());

      setTodayAppts(dedup);
      setTotal(Number(resp.data?.meta?.total ?? dedup.length));
    } catch (e: any) {
      console.error(e);
      setErrorToday(e?.response?.data?.detail ?? e?.message ?? "Failed to load waiting room");
    } finally {
      setLoadingToday(false);
    }
  };

  if (activeTab === "today") fetchData();
}, [activeTab, page, perPage, clinician, consent, selectedPatientId, consentStatusParam]);

  // Future appointments call
  useEffect(() => {
    const fetchReminders = async () => {
      setLoadingRem(true);
      setErrorRem(null);
      try {
        const params: Record<string, any> = {
          page,
          per_page: perPage,
        };
        if (clinician !== "all") params.clinician = clinician;

        const base = process.env.REACT_APP_BASE_URL_V2;
        
      if (selectedPatientId) params.patient = selectedPatientId;
       if (consentStatusParam) params.consent_status = consentStatusParam;
        const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/future-appointments`;

        const resp = await axios.get(endpoint, {
          params,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
          ? resp.data
          : [];

         const normalized = (payload as any[]).map((r, i) => toAppointment(r, i));
        const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());
        setReminderAppts(dedup);
        setTotalRem(Number(resp.data?.meta?.total ?? dedup.length));
      } catch (e: any) {
        console.error(e);
        setErrorRem(e?.response?.data?.detail ?? e?.message ?? "Failed to load future appointments");
      } finally {
        setLoadingRem(false);
      }
    };

    if (activeTab === "reminders") {
      fetchReminders();
    }
  }, [activeTab, page, perPage, clinician,selectedPatientId,consentStatusParam]);

  // Ready for upload call
  useEffect(() => {
    const fetchUpload = async () => {
      setLoadingUpload(true);
      setErrorUpload(null);
      try {
        const params: Record<string, any> = {
          page: pageUpload,
          per_page: perPageUpload,
        };
        if (clinician !== "all") params.clinician = clinician;

        const base = process.env.REACT_APP_BASE_URL_V2;
        if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

        if (selectedPatientId) params.patient = selectedPatientId;
         if (consentStatusParam) params.consent_status = consentStatusParam;
        const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/ready-for-upload`;

        const resp = await axios.get(endpoint, {
          params,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });


        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
          ? resp.data
          : [];

        const normalized = (payload as any[]).map((r, i) => toUploadRow(r, i));
        const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());

        setUploadRows(dedup);
        setTotalUpload(Number(resp.data?.meta?.total ?? dedup.length));
      } catch (e: any) {
        console.error(e);
        setErrorUpload(e?.response?.data?.detail ?? e?.message ?? "Failed to load ready-for-upload");
      } finally {
        setLoadingUpload(false);
      }
    };

    if (activeTab === "upload") fetchUpload();
  }, [activeTab, pageUpload, perPageUpload, clinician,selectedPatientId,consentStatusParam]);

  // all
useEffect(() => {
  const fetchAll = async () => {
    setLoadingAll(true);
    setErrorAll(null);

    try {
      const base = process.env.REACT_APP_BASE_URL_V2;

      const params: Record<string, any> = {
        page: pageAll,
        per_page: perPageAll,
      };

      if (clinician !== "all") params.clinician = clinician;   
      if (selectedPatientId) params.patient = selectedPatientId;
      if (consentStatusParam) params.consent_status = consentStatusParam;

      const endpoint = `${base}dashboard/all`;

      const resp = await axios.get(endpoint, {
        params,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const payload = Array.isArray(resp.data?.data)
        ? resp.data.data
        : Array.isArray(resp.data)
        ? resp.data
        : [];

      const normalized = (payload as any[]).map((r, i) => toAppointment(r, i));
      const dedup = Array.from(new Map(normalized.map((a) => [a.id, a])).values());

      setAllRows(dedup);
      setTotalAll(Number(resp.data?.meta?.total ?? dedup.length));
    } catch (e: any) {
      console.error(e);
      setErrorAll(e?.response?.data?.detail ?? e?.message ?? "Failed to load all dashboard data");
    } finally {
      setLoadingAll(false);
    }
  };

  if (activeTab === "all") fetchAll();
}, [activeTab, pageAll, perPageAll, clinician, selectedPatientId, consentStatusParam, statusFilter]);

  // get user + clinicians
  useEffect(() => {
    const getUserData = async () => {
      try {
        const { data } = await axios.get("/user", { headers: { Accept: "application/json" } });

        const u =
          Array.isArray(data)
            ? (typeof data[1] === "object" && data[1] !== null ? data[1] : data[0])
            : data;

        setUser(u);

        if (u?.medicube_practice_id) {
          await getClinicians(u.medicube_practice_id);
        } else {
          console.warn("No medicube_practice_id on user payload:", u);
        }

        return u;
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const getClinicians = async (practiceId: number) => {
      try {
        const { data } = await axios.get("/users/dropdown/clinicians", {
          params: { practice: practiceId },
          headers: { Accept: "application/json" },
        });

        const arr = Array.isArray(data) ? data : [];
        setClinicians(arr.map((c: any) => ({ value: String(c.value), label: String(c.label) })));
        return arr;
      } catch (error) {
        console.error("Error fetching clinicians:", error);
      }
    };

    getUserData();
  }, []);

 // ---- Filters for all ----
 const filteredAll = useMemo(() => {
  return allRows.filter((a) => {
    const s = (a.status || a.consentStatus || "").toLowerCase();
    const stOk = statusFilter === "all" || s.includes(statusFilter.toLowerCase());
    const qOk =
      !query ||
      a.patient.toLowerCase().includes(query.toLowerCase()) ||
      a.clinician.toLowerCase().includes(query.toLowerCase());
    return qOk && stOk;
  });
  }, [allRows, query, statusFilter]);

  // ---- Filters for Today ----
  const todayFiltered = useMemo(() => {
  return todayAppts.filter((a) => {
    // --- Consent filter (correct & consistent) ---
    const csOk =
      consent === "all" ||
      (consent === "signed" && a.consentStatus === "SIGNED") ||
      (consent === "not_signed" && a.consentStatus === "NOT_SIGNED") ||
      (consent === "email_bounced" && a.consentStatus === "EMAIL_BOUNCED");

    // --- Search filter ---
    const qOk =
      !query ||
      a.patient.toLowerCase().includes(query.toLowerCase()) ||
      a.clinician.toLowerCase().includes(query.toLowerCase());

    // --- Status filter ---
    const s = (a.status || a.consentStatus || "").toLowerCase();

    const stOk =
      statusFilter === "all" ||
      s.includes(statusFilter.toLowerCase());

    return csOk && qOk && stOk;
  });
}, [consent, query, todayAppts, statusFilter]);

  // ---- Filters for Reminders ----
  const remindersFiltered = useMemo(() => {
  const rows = reminderAppts.map((a) => ({
    ...a,
    _days: a.date ? daysUntil(a.date) : 0,
  }));

  return rows.filter((a) => {
    // ✅ HIDE DRAFTS in Send Reminder tab
    const statusLower = String(a.status || "").toLowerCase().trim();
    if (statusLower === "draft") return false;

    // Search
    const qOk =
      !query ||
      a.patient.toLowerCase().includes(query.toLowerCase()) ||
      a.clinician.toLowerCase().includes(query.toLowerCase());

    // Consent filter (matching all tabs)
    const csOk =
      consent === "all" ||
      (consent === "signed" && a.consentStatus === "SIGNED") ||
      (consent === "not_signed" && a.consentStatus === "NOT_SIGNED") ||
      (consent === "email_bounced" && a.consentStatus === "EMAIL_BOUNCED");

    // Status filter (still works)
    const s = (a.status || a.consentStatus || "").toLowerCase();
    const stOk = statusFilter === "all" || s.includes(statusFilter.toLowerCase());

    return qOk && csOk && stOk;
  });
}, [reminderAppts, query, consent, statusFilter]);

  // ---- Filters for Upload ----
  const uploadFiltered = useMemo(() => {
  const rows = uploadRows;

  return rows.filter((r) => {
    const clOk = clinician === "all" || r.clinician === clinician;

    const qOk =
      !query ||
      r.patient.toLowerCase().includes(query.toLowerCase()) ||
      r.clinician.toLowerCase().includes(query.toLowerCase());

    const st = String(statusFilter || "all"); 
    const s = String(r.status || "").toLowerCase();
    const isArchived = r.isArchived === true || r.raw?.is_archived === true;

    const stOk =
      st === "all"
        ? true
        : st === "Archived"
          ? isArchived
          : !isArchived && s.includes(st.toLowerCase()); 

    return clOk && qOk && stOk;
  });
}, [uploadRows, clinician, query, statusFilter]);


useEffect(() => {
  if (!autoClearFilters) return;

  setPatientQuery("");        
  setSelectedPatientId(null); 
  setQuery("");               
  setConsentStatusParam(undefined);
  setStatusFilter("all");
  setClinician("all");

  setPage(1);
  setPageUpload(1);
  setPageAll(1);
}, [activeTab, autoClearFilters]);

const statusMap = {
  all: undefined,
  Draft: "Draft",
  "Sent For Signing": "Sent For Signing",
  Expired: "Expired",
  Completed: "Completed",
  Archived: "Archived",
};

  // ---- Select helpers ----
  const toggleReminderRow = (id: string) =>
    setReminderSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
  });

  const toggleReminderAll = () => {
    const allIds = remindersFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => reminderSelected.has(id));
    setReminderSelected(allSelected ? new Set() : new Set(allIds));
  };

  const toggleUploadRow = (id: string) =>
    setUploadSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
  });

  const toggleUploadAll = () => {
    const allIds = uploadFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => uploadSelected.has(id));
    setUploadSelected(allSelected ? new Set() : new Set(allIds));
  };

  const openQr = (a: Appointment) => {
    setSelectedAppt(a);
    setQrOpen(true);
  };

  // ---- Archive helpers ----
  const askArchive = (id: string | number, name: string, source: "upload" | "reminders") => {
    setConfirmArchive({ open: true, id, name, source, loading: false });
  };

  const doArchive = async () => {
  if (!confirmArchive.id) return;

  try {
    setConfirmArchive((p) => ({ ...p, loading: true }));

    await axios.delete(
      `consent_bundle/${confirmArchive.id}/archive_consent_bundle`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    // Optimistic remove from current list
    if (confirmArchive.source === "upload") {
      setUploadRows((prev) =>
        prev.filter((r) => String(r.raw?.id ?? r.id) !== String(confirmArchive.id))
      );
    } else if (confirmArchive.source === "reminders") {
      setReminderAppts((prev) =>
        prev.filter((r) => String(r.id) !== String(confirmArchive.id))
      );
    }

    showNotify("success", "Archived successfully!");
  } catch (e: any) {
    console.error("Archive failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to archive bundle");
  } finally {
    setConfirmArchive({ open: false, source: null, loading: false });
  }
};

const navigate = useNavigate();

const openConsentBundle = async (bundleId) => {
  try {
    const res = await axios.get(`/consent_bundle/${bundleId}`);
    const data = res.data;

    if (!data?.status) {
       toast.error("No status found for this consent bundle");
      
      return;
    }

    const status = data.status.toLowerCase();

    if (["completed", "signed", "expired"].includes(status)) {
      // ✅ Completed ones should open Summary
      navigate(`/summary-new/${bundleId}`);
      return;
    }

    if (status === "draft") {
      sessionStorage.setItem("activeTreatmentData", JSON.stringify(data));
      navigate(`/new-consentBundle?step=1`);
    } else if (
      status.includes("sent") ||
      status.includes("unsigned") ||
      status.includes("sign")
    ) {
      navigate(`/summary-new/${bundleId}`);
    } else {
      toast.error(`Cannot open — this consent is ${data.status}`);
    }
  } catch (err) {
    console.error("Failed to fetch consent bundle:", err);
     toast.error("Error loading consent bundle");
   
  }
};

  // ---------- Renders ----------
  const Tabs = (
    <div className={`${styles.tabs} tutorial-tabs`}>
    <button
      className={`${styles.tab} ${activeTab === "all" ? styles.tabActive : ""}`}
      onClick={() => setActiveTab("all")}
    >
      All
    </button>
      <button
        className={`${styles.tab} ${activeTab === "today" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("today")}
      >
        Due today
      </button>
      <button
        className={`${styles.tab} ${activeTab === "reminders" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("reminders")}
      >
        Send reminder
      </button>
      <button
        className={`${styles.tab} ${activeTab === "upload" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("upload")}
      >
        Ready for upload
      </button>
      
    </div>
  );

// ---- Bulk reminder sender ----
const doBulkReminders = async () => {
  if (reminderSelected.size === 0) return;

  try {
    
    const ids = reminderAppts
      .filter((a) => reminderSelected.has(a.id))
      .map((a) => Number(a.raw?.id ?? a.id)); 

    if (ids.length === 0) {
      toast.error("No valid consent bundle IDs found.");
      return;
    }

    const base = process.env.REACT_APP_BASE_URL_V2;
    if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

    const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/bulk_send_reminders`;

    await axios.post(
      endpoint,
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    // Clear selection after success
    setReminderSelected(new Set());
    toast.success("Reminders sent successfully!");
  } catch (e: any) {
    console.error("Bulk reminders failed", e);
    toasting.error(e?.response?.data?.detail ?? "Failed to send reminders");
  }
};

const sendReminders = async (ids: number[]) => {
  if (!ids.length) return;

  const base = process.env.REACT_APP_BASE_URL_V2;
  if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

  const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/bulk_send_reminders`;

  try {
    await axios.post(
      endpoint,
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    toast.success("Reminders sent successfully!");

  } catch (e: any) {
    console.error("Send reminders failed", e);
    toast.error(e?.response?.data?.detail ?? "Failed to send reminders");
  }
};

const doBulkDownload = async () => {
  if (uploadSelected.size === 0) return;

  try {
    setIsBulkDownloading(true);   // <-- SHOW LOTTIE FULLSCREEN

    const ids = uploadFiltered
      .filter((u) => uploadSelected.has(u.id))
      .map((u) => Number(u.raw?.id ?? u.id));

    if (!ids.length) {
      setIsBulkDownloading(false);
      showNotify("error", "No valid consent bundle IDs found.");
      return;
    }

    const base = process.env.REACT_APP_BASE_URL_V2;
    if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

    const endpoint = `${base}dashboard/bulk_download`;

    const resp = await axios.post(
      endpoint,
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "consent_bundles.zip");
    document.body.appendChild(link);
    link.click();
    link.remove();

    setUploadSelected(new Set());

    showNotify("success", "Downloaded successfully!");

  } catch (e: any) {
    console.error("Bulk download failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to download bundles");

  } finally {
    setIsBulkDownloading(false);  // <-- HIDE LOTTIE FULLSCREEN
  }
};

const doBulkArchiveUpload = async () => {
  if (uploadSelected.size === 0) return;

  try {
    const ids = uploadFiltered
      .filter((u) => uploadSelected.has(u.id))
      .map((u) => Number(u.raw?.id ?? u.id));

    if (!ids.length) {
      showNotify("error", "No valid consent bundle IDs found.");
      return;
    }

    showNotify("archive", "Archiving...");

    const base = process.env.REACT_APP_BASE_URL_V2;
    if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

   const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/bulk_archive`;

    await axios.put(
      endpoint,
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    setUploadRows((prev) =>
      prev.filter((r) => !ids.includes(Number(r.raw?.id ?? r.id)))
    );

    setUploadSelected(new Set());

    showNotify("success", "Archived successfully!");
  } catch (e: any) {
    console.error("Bulk archive failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to archive bundles");
  }
};

// ---- Notification system ----
type NotifyType = "archive" | "download" | "success" | "error";

const [notify, setNotify] = useState<{
  show: boolean;
  type: NotifyType;
  message: string;
}>({
  show: false,
  type: "success",
  message: "",
});

const showNotify = (type: NotifyType, msg: string) => {
  setNotify({ show: true, type, message: msg });
  setTimeout(() => setNotify({ show: false, type: "success", message: "" }), 2000);
};

useEffect(() => {
  const checkGuestReference = async () => {
    const guestRef = localStorage.getItem("guest_purchase_reference");
    if (!guestRef) return;

    try {
      console.log("Found guest purchase reference:", guestRef);

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = new URL(
        "onboarding/get_stripe_payment_link_via_guest_reference",
        base
      ).toString();

      const resp = await axios.post(
        endpoint,
        JSON.stringify(guestRef),
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (
        resp.data &&
        typeof resp.data === "string" &&
        resp.data.startsWith("http")
      ) {
        console.log("Redirecting to Stripe link:", resp.data);
        window.location.replace(resp.data);
      } else {
        console.log("Received response:", resp.data);
      }

      localStorage.removeItem("guest_purchase_reference");
    } catch (err) {
      console.error(
        "Failed to get Stripe payment link via guest reference:",
        err
      );
    }
  };

  checkGuestReference();
}, []);

const handleTodayRowClick = (a) => {
  const bundleId = Number(a.bundleId ?? a.id);

  if (a.consentStatus === "EMAIL_BOUNCED") {
    toast.error("Cannot open — email bounced");
    return;
  }

  if (a.consentStatus === "NOT_SIGNED") {
    openConsentBundle(bundleId);
    return;
  }

  if (a.consentStatus === "SIGNED") {
    openConsentSummary(bundleId);
    return;
  }

  toast.error(`Cannot open — this consent is ${a.consentStatus}`);
};

useEffect(() => {
  if (!patientQuery) {
    setPatientOptions([]);
    return;
  }

  const delay = setTimeout(async () => {
    try {
      setLoadingPatients(true);

      const base = process.env.REACT_APP_BASE_URL;
      const endpoint = `${base}patient`;

      const resp = await axios.get(endpoint, {
        params: { query: patientQuery },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      // EXACT RESPONSE YOU SHOWED
      const arr = Array.isArray(resp.data) ? resp.data : resp.data?.data || [];

      const formatted = arr.map((p) => ({
  id: p.dentally_id,  
  label: `${p.first_name?.trim() || ""} ${p.last_name?.trim() || ""}`,
  raw: p,
}));

      setPatientOptions(formatted);
    } catch (err) {
      console.error("Patient search error:", err);
    } finally {
      setLoadingPatients(false);
    }
  }, 300);

  return () => clearTimeout(delay);
}, [patientQuery]);

const toggleAllSelectAllTab = () => {
  const newSet = new Set();
  if (selectedAllTab.size !== filteredAll.length) {
    filteredAll.forEach(a => newSet.add(a.id));
  }
  setSelectedAllTab(newSet);
};

// select single row
const toggleSingleSelectAllTab = (id) => {
  const copy = new Set(selectedAllTab);
  if (copy.has(id)) copy.delete(id);
  else copy.add(id);
  setSelectedAllTab(copy);
};

// SINGLE download
const downloadSingleAll = async (id) => {
  try {
    setDownloadingIds(prev => new Set(prev).add(id));

    const url = `/consent_bundle/${id}/download_full_consent_bundle_report`;

    const resp = await axios.get(url, { responseType: "blob" });

    const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `consent_bundle_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();

  } catch (err) {
    toast.error("Failed to download file");
  } finally {
    setDownloadingIds(prev => {
      const s = new Set(prev);
      s.delete(id);
      return s;
    });
  }
};

// BULK download
const downloadBulkAll = async () => {
  if (selectedAllTab.size === 0) return;

  try {
    setBulkDownloading(true);

    const endpoint = `${process.env.REACT_APP_BASE_URL_V2}dashboard/bulk_download`;

    const resp = await axios.post(
      endpoint,
      { consent_bundle_ids: Array.from(selectedAllTab) },
      { responseType: "blob" }
    );

    const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "consent_bundles.zip";
    link.click();
    link.remove();

  } catch (err) {
    toast.error("Bulk download failed");
  } finally {
    setBulkDownloading(false);
  }
};

// --- Fetch interaction log ---
const openQuickView = async (id) => {
  setQuickViewId(id);
  setQuickViewOpen(true);
  setQuickViewLoading(true);
  setQuickViewError("");
  setInteractionLog({});

  try {
    const endpoint = `/consent_bundle/${id}/interaction_log`;

    const res = await axios.get(endpoint);

    setInteractionLog(res.data);
  } catch (err) {
    setQuickViewError("Failed to load interaction log");
  } finally {
    setQuickViewLoading(false);
  }
};

const canShowQr = (status?: string) => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes("sign") || s === "completed";
};


const getSelectedReminderBundleIds = () => {
  return remindersFiltered
    .filter((r) => reminderSelected.has(r.id))
    .map((r) => Number(r.raw?.id ?? r.id))
    .filter((n) => Number.isFinite(n));
};

const bulkSendDisabled =
  reminderSelected.size === 0 ||
  remindersFiltered.some(
    (r) => reminderSelected.has(r.id) && r.status?.toLowerCase() === "draft"
  );


  return (
    <>
      <Header />
      <div className={styles.wrap}>
        {/* Page Header (varies by tab) */}
        {activeTab === "reminders" ? (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Future appointment</h1>
              <div className={styles.subhead}>
                <FiCalendar />
                <span>Up and coming appointments requiring attention</span>
              </div>
            </div>
          </header>
        ) : activeTab === "upload" ? (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Ready for upload</h1>
              <div className={styles.subhead}>
                <FiCalendar />
                <span>Completed and past appointments</span>
              </div>
            </div>
          </header>
        ) : (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Waiting room</h1>
              <div className={styles.date}>
                <FiMonitor />
                <span>{todayLong}</span>
              </div>
            </div>
            <div className={styles.total}>
              <span>Total appointments</span>
              <strong>{todayAppts.length}</strong>
            </div>
          </header>
        )}

        {/* Filters */}
        <section className={`${styles.filters} tutorial-filters`}>
            <h3 className={styles.filtersTitle}>Filters</h3>
            {/* button */}
            <div className={styles.filterToggle}>
              <label className={styles.toggleLabel}>
                <span className={styles.toggleText}>Clear filters on tab change</span>

                <div
                  className={`${styles.toggleSwitch} ${
                    autoClearFilters ? styles.toggleOn : ""
                  }`}
                  onClick={() => setAutoClearFilters(!autoClearFilters)}
                >
                  <div className={styles.toggleKnob}></div>
                </div>
              </label>
            </div>


            <div className={styles.filtersRow}>
              <div className={styles.filter}>
                <label>Clinician</label>
                <div className={styles.select}>
                  <select
                    value={clinician}
                    onChange={(e) => {
                      setPage(1);
                      if (activeTab === "upload") setPageUpload(1);
                      setClinician(e.target.value);
                    }}
                  >
                    <option value="all">All clinicians</option>
                    {clinicians.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span className={styles.selectCaret}>▾</span>
                </div>
              </div>

              <div className={styles.filter}>
                <label>Status</label>

                <div className={styles.select}>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      const uiValue = e.target.value;

                      setStatusFilter(uiValue);

                      if (uiValue === "All") {
                        setConsentStatusParam(undefined); 
                      } else {
                        setConsentStatusParam(statusMap[uiValue]); 
                      }

                      setPage(1);
                      setPageUpload(1);
                      setPageAll(1);
                    }}
                  >
                    <option value="all">All</option>
                    {activeTab !== "reminders" && <option value="Draft">Draft</option>}
                    <option value="Sent For Signing">Sent For Signing</option>
                    <option value="Completed">Completed</option>
                    <option value="Expired">Expired</option>
                    <option value="Archived">Archived</option>
                  </select>
                  <span className={styles.selectCaret}>▾</span>
                </div>
              </div>


              {/* search patient */}
              <div className={styles.filter}>

            <div className={styles.filterInputWrapper}>
            <Autocomplete
                    options={patientOptions}
                    loading={loadingPatients}

                    value={
                      selectedPatientId
                        ? patientOptions.find((p) => p.id === selectedPatientId) || null
                        : null
                    }

                    inputValue={patientQuery}

                    getOptionLabel={(opt) => opt?.label || ""}

                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedPatientId(newValue.id);     
                        setPatientQuery(newValue.label);       
                      } else {
                        setSelectedPatientId(null);
                      }

                      setPage(1);
                      if (activeTab === "upload") setPageUpload(1);
                    }}


                    onInputChange={(event, newInputValue, reason) => {
                    if (reason === "clear") {
                      setSelectedPatientId(null);
                      setPatientQuery("");
                      return;
                    }
                    if (reason === "blur" || reason === "reset") {
                      return;
                    }
                    setPatientQuery(newInputValue);
                  }}


                    noOptionsText="No patients found"

                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search by patient"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            background: "#fff",
                            borderRadius: "10px",
                            border: "1px solid #E0E0E0",
                            height: "42px",
                            paddingLeft: "38px",
                            fontWeight: 500,
                            boxShadow: "none",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root fieldset": { border: "none" },
                          "& input::placeholder": { color: "#666", opacity: 1 },
                        }}
                      />
                    )}
                  />
            </div>
          </div>

            </div>
        </section>

        {/* Card + Tabs */}
        <section className={styles.card}>
          {Tabs}

          {/* Wrap tab content to animate on tab change */}
          <div key={activeTab} className={styles.animIn}>
              {/* ---- Today / Waiting room ---- */}
              {activeTab === "today" && (
                <>
                  <h4 className={styles.sectionTitle}>Today's appointment</h4>

                <div className={`${styles.table} tutorial-table`}>
                    <div className={`${styles.grid} ${styles.tableHead}`}>
                      <div>Time</div>
                      <div>Patient name</div>
                      <div>Date of birth</div>
                      <div>Clinician</div>
                      <div>Consent status</div>
                      <div>Emails</div>
                    <div>Texts</div>
                      <div>Action</div>
                    </div>

                    <div className={styles.tableBody}>
                      {loadingToday && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                            Loading waiting room…
                          </div>
                        </div>
                      )}

                      {errorToday && !loadingToday && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                            {errorToday}
                          </div>
                        </div>
                      )}

                      {!loadingToday && !errorToday && todayFiltered.length === 0 && (
                        <div className={styles.rowSoft}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1", opacity: 0.7 }}>
                            No data matches your filters.
                          </div>
                        </div>
                      )}


                      {!loadingToday && !errorToday && todayFiltered.map((a) => {
                        const rowState =
                          a.consentStatus === "EMAIL_BOUNCED"
                            ? styles.rowBounced
                            : a.consentStatus === "NOT_SIGNED"
                            ? styles.rowNotSigned
                            : "";
                        return (
                          <div
                              className={`${styles.row} ${rowState} ${styles.grid}`}
                              key={a.id}
                              onClick={() => handleTodayRowClick(a)}
                              style={{ cursor: "pointer" }}
                            >
                            <div className={styles.cell}>
                              <span className={styles.time}><FiClock />{a.time}</span>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.patient}>
                                <span className={styles.avatar}>
                                  {a.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                <span className={styles.patientName}>{a.patient}</span>
                              </div>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.patient}>
                                <span className={styles.patientName}>{a.date_of_birth}</span>
                              </div>
                            </div>

                            <div className={styles.cell}>{a.clinician}</div>

                            <div className={styles.cell}>
                              <span
                                className={`${styles.badge} ${
                                  a.status?.toLowerCase() === "draft"
                                    ? styles.badgeGray
                                    : a.status?.toLowerCase() === "expired"
                                    ? styles.badgeBlack
                                    : a.status?.toLowerCase() === "completed"
                                    ? styles.badgeSigned
                                    : a.status?.toLowerCase().includes("sign")
                                    ? styles.badgeYellow
                                    : styles.badgeNotSigned
                                }`}
                              >
                                {a.status ?? "—"}
                              </span>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.chip}><FiMail /><span>{a.emails}</span></div>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.chip}><FiMessageCircle /><span>{a.texts}</span></div>
                            </div>

                            <div className={`${styles.cell} ${styles.actions}`}>
                                <button
                                  className={`${styles.btn} ${styles.btnLight} tutorial-qr`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openQr(a);
                                  }}
                                >
                                  <FiGrid /> QR Code
                                </button>

                                {a.consentStatus !== "SIGNED" ? (
                                <button
                                    className={`${styles.btn} ${styles.btnOutlineBlue}`}
                                    disabled={a.status?.toLowerCase() === "draft"}   
                                    onClick={(e) => {
                                      e.stopPropagation(); 
                                      const id = Number(a.raw?.id ?? a.id);
                                      setPendingReminderIds([id]);
                                      setShowConfirmModal(true);
                                    }}
                                  >
                                    <FiSend /> Send reminder
                                  </button>

                                ) : (
                                  <button
                                    className={`${styles.btn} ${styles.btnPill} ${styles.btnWhite}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sendEmail?.(a); 
                                    }}
                                  >
                                    Email
                                  </button>
                                )}
                              </div>

                          </div>
                        );
                      })}
                    </div>
                    <div className={styles.tableFooter}>
                      <div className={styles.pagerInfo}>
                        Page {page} • Per page
                        <select
                          value={perPage}
                          onChange={(e) => { setPage(1); setPerPage(parseInt(e.target.value, 10)); }}
                          style={{ marginLeft: 8 }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                        <span style={{ marginLeft: 12 }}>Total: {total}</span>
                      </div>
                      <div className={styles.pagerBtns}>
                        <button
                          className={styles.btn}
                          disabled={page <= 1 || loadingToday}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                        >
                          Prev
                        </button>
                        <button
                          className={styles.btn}
                          disabled={loadingToday || (todayAppts.length < perPage && page * perPage >= total)}
                          onClick={() => setPage(p => p + 1)}
                          style={{ marginLeft: 8 }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* ---- All appointments ---- */}
              {activeTab === "all" && (
                <>
                  <h4 className={styles.sectionTitle}>All Appointments Overview</h4>
                  <div className={styles.sectionHeaderRow}>
                 
                   {selectedAllTab.size > 0 && (
                      <div className={styles.bulkDownloadFloating}>
                        
                        {bulkDownloading ? (
                          <div className={styles.lottieWrapper}>
                            <Lottie 
                              animationData={bulkDownloadAnim} 
                              loop 
                              style={{ width: 80, height: 80 }}
                            />
                          </div>
                        ) : (
                          <button
                            className={styles.btnPrimary}
                            onClick={downloadBulkAll}
                          >
                            Download selected ({selectedAllTab.size})
                          </button>
                        )}

                      </div>
                    )}
                
                  </div>

                  <div className={styles.table}>
                    
                    {/* TABLE HEADER */}
                    <div className={`${styles.gridRemAll} ${styles.tableHead}`}>
                    
                      <div>
                        <input
                          type="checkbox"
                          checked={
                            filteredAll.length > 0 &&
                            selectedAllTab.size === filteredAll.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleAllSelectAllTab();
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div>Appointment date</div>
                      <div>Patient name</div>
                      <div>Date of Birth</div>
                      <div>Clinician</div>
                      <div>Status</div>
                      <div>Days until</div>
                      <div>Email</div>
                      <div>SMS</div>
                      <div>Signed date</div>
                      <div>Actions</div>
                    </div>

                    <div className={styles.tableBody}>

                      {/* LOADING */}
                      {loadingAll && (
                        <div className={`${styles.rowSoft} ${styles.gridRemAll}`}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                            Loading all appointments…
                          </div>
                        </div>
                      )}

                      {/* ERROR */}
                      {errorAll && !loadingAll && (
                        <div className={`${styles.rowSoft} ${styles.gridRemAll}`}>
                          <div
                            className={styles.cell}
                            style={{ gridColumn: "1 / -1", color: "crimson" }}
                          >
                            {errorAll}
                          </div>
                        </div>
                      )}

                      {/* ROWS */}
                      {!loadingAll && !errorAll && filteredAll.map((a) => (
                        <div
                          className={`${styles.rowSoft} ${styles.gridRemAll}`}
                          key={a.id}
                          onClick={() => openConsentBundle(a.raw?.id ?? a.id)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Checkbox */}
                          <div className={styles.cell} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {/* BIG CHECKBOX */}
                            <input
                              className={styles.checkboxLarge}
                              type="checkbox"
                              checked={selectedAllTab.has(a.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSingleSelectAllTab(a.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />

                              {(!a.patient_email || a.patient_email.trim() === "") && (
                              <Tooltip title="No email">
                                <span className={styles.noEmailIcon}>
                                  <UnsubscribeIcon />
                                </span>
                              </Tooltip>
                            )}
                          </div>


                          {/* Appointment date */}
                          <div className={styles.cell}>
                            {a.date ? fmtShort(new Date(a.date)) : "-"}
                          </div>

                          {/* Patient */}
                          <div className={styles.cell}>
                            <div className={styles.patient}>
                              <div className={styles.avatar}>
                                {a.patient?.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                              </div>
                              <span className={styles.patientName}>{a.patient}</span>
                            </div>
                          </div>

                          {/* DOB */}
                          <div className={styles.cell}>
                            {a.date_of_birth}
                          </div>

                          {/* Clinician */}
                          <div className={styles.cell}>{a.clinician}</div>

                          {/* Status */}
                          <div className={styles.cell}>
                            <span className={`${styles.badge} ${
                              a.status?.toLowerCase() === "draft"
                                ? styles.badgeGray
                                : a.status?.toLowerCase() === "expired"
                                ? styles.badgeBlack
                                : a.status?.toLowerCase() === "completed"
                                ? styles.badgeSigned
                                : a.status?.toLowerCase().includes("sign")
                                ? styles.badgeYellow
                                : styles.badgeNotSigned
                            }`}>
                              {a.status ?? "—"}
                            </span>
                          </div>

                          {/* Days until */}
                          <span className={`${styles.pillDays} ${
                            a.daysUntilAppointment <= 3 ? styles.pillDaysOverdue
                            : a.daysUntilAppointment <= 7 ? styles.pillDaysSoon
                            : styles.pillDaysSafe
                          }`}>
                            {a.daysUntilAppointment ?? 0} DAYS
                          </span>

                          {/* Email */}
                          <div className={styles.cell}>
                            <div className={styles.iconWithNum}>
                              <FiMail />
                              <span>{a.emails}</span>
                            </div>
                          </div>

                          {/* SMS */}
                          <div className={styles.cell}>
                            <div className={styles.iconWithNum}>
                              <FiMessageCircle />
                              <span>{a.texts}</span>
                            </div>
                          </div>

                          {/* Signed date */}
                          <div className={styles.cell}>
                            {a.signed_date ? fmtShort(new Date(a.signed_date)) : "Not signed"}
                          </div>

                          {/* ACTIONS */}
                          <div
                            className={`${styles.cell} ${styles.actionsAll}`}
                            style={{ display: "flex", gap: 8, alignItems: "center" }}
                          >
                            {canShowQr(a.status) && (
                                <button
                                  className={`${styles.btn} ${styles.btnLight}`}
                                  title="QR Code"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openQr(a);
                                  }}
                                >
                                  <FiGrid />
                                </button>
                              )}

                            {/* 👁 Quick View */}
                            <button
                              className={`${styles.btn} ${styles.btnLight}`}
                              title="Quick view"
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuickView(a.raw?.id ?? a.id);
                              }}
                            >
                              <FiEye /> 
                            </button>


                        {a.status?.toLowerCase() === "draft" && a.versions?.length > 0 && (
                          <Tooltip title="Revert to previous version">
                            <button
                              className={`${styles.btn} ${styles.btnOutlineBlue}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openRevertModal(a); 
                              }}
                            >
                              <FiRotateCcw />
                            </button>
                          </Tooltip>
                        )}

                          <Tooltip title="Send reminder">
                            <span>
                              <button
                                className={`${styles.btn} ${styles.btnOutlineBlue}`}
                                disabled={a.status?.toLowerCase() === "draft"}   
                                onClick={(e) => {
                                  e.stopPropagation(); 
                                  const id = Number(a.raw?.id ?? a.id);
                                  setPendingReminderIds([id]);
                                  setShowConfirmModal(true);
                                }}
                              >
                                <FiSend /> 
                              </button>
                            </span>
                          </Tooltip>


                            {/* Download */}
                            <button
                              className={`${styles.btn} ${styles.btnLight}`}
                              title="Download"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadSingleAll(a.raw?.id ?? a.id);
                              }}
                            >
                              {downloadingIds.has(a.id) ? (
                                <span className={styles.spinner}></span>
                              ) : (
                                <>
                                  <FiDownload /> 
                                </>
                              )}
                            </button>

                            {/* Archive */}
                            <button
                            className={`${styles.btn} ${styles.btnLight}`}
                            title="Archive"
                            onClick={(e) => {
                              e.stopPropagation();
                              askArchive(a.id, a.patient, "all");
                            }}
                          >
                            <FiArchive />
                          </button>
                          </div>
                        </div>
                      ))}

                      {/* No data */}
                      {!loadingAll && !errorAll && filteredAll.length === 0 && (
                        <div className={`${styles.rowSoft} ${styles.gridRemAll}`}>
                          <div
                            className={styles.cell}
                            style={{ gridColumn: "1 / -1", opacity: 0.7 }}
                          >
                            No data matches your filters.
                          </div>
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Pagination */}
                  <div className={styles.tableFooter}>
                    <div className={styles.pagerInfo}>
                      Page {pageAll} • Per page
                      <select
                        value={perPageAll}
                        onChange={(e) => {
                          setPageAll(1);
                          setPerPageAll(parseInt(e.target.value, 10));
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span style={{ marginLeft: 12 }}>Total: {totalAll}</span>
                    </div>

                  <div className={styles.pagerBtns}>

                  {selectedAllTab.size > 0 && (
                      <div className={styles.bulkDownloadFloating}>
                        
                        {/* While downloading show Lottie */}
                        {bulkDownloading ? (
                          <div className={styles.lottieWrapper}>
                            <Lottie 
                              animationData={bulkDownloadAnim} 
                              loop 
                              style={{ width: 80, height: 80 }}
                            />
                          </div>
                        ) : (
                          <button
                            className={styles.btnPrimary}
                            onClick={downloadBulkAll}
                          >
                            Download selected ({selectedAllTab.size})
                          </button>
                        )}

                      </div>
                    )}
                    
                      <button
                        className={styles.btn}
                        disabled={pageAll <= 1 || loadingAll}
                        onClick={() => setPageAll((p) => Math.max(1, p - 1))}
                      >
                        Prev
                      </button>
                      <button
                        className={styles.btn}
                        disabled={loadingAll || pageAll * perPageAll >= totalAll}
                        onClick={() => setPageAll((p) => p + 1)}
                        style={{ marginLeft: 8 }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
              {/* ---- Send reminder ---- */}
              {activeTab === "reminders" && (
                <>
                <div className={styles.sectionHeaderRow}>
                    <h4 className={styles.sectionTitle}>Send reminders</h4>

                    {reminderSelected.size > 0 && (
                      <button
                        className={styles.bulkBtnTop}
                        disabled={bulkSendDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          const ids = getSelectedReminderBundleIds();
                          if (!ids.length) {
                            toast.error("No valid consent bundle IDs found.");
                            return;
                          }
                          setPendingReminderIds(ids);
                          setShowConfirmModal(true);
                        }}
                      >
                        <FiSend style={{ marginRight: 6 }} />
                        Bulk send ({reminderSelected.size})
                      </button>
                    )}
                  </div>
                  <div className={styles.table}>
                    <div className={`${styles.gridRem} ${styles.tableHead}`}>
                      <div>
                        <Tooltip title="Select all items">
                          <div className={styles.checkboxRow}>
                          <input
                              type="checkbox"
                              checked={remindersFiltered.length > 0 && remindersFiltered.every(r => reminderSelected.has(r.id))}
                              onClick={(e) => e.stopPropagation()}   
                              onChange={toggleReminderAll}
                            />
                            <span className={styles.checkboxLabel}></span>
                          </div>
                        </Tooltip>
                      </div>
                      <div>Date / Time</div>
                      <div>Patient name</div>
                      <div>Date of Birth</div>
                      <div>Clinician</div>
                      <div>Days until</div>
                      <div>Consent status</div>
                      <div>Reminders</div>
                      <div>Last reminder</div>
                      <div>Actions</div>
                    </div>

                    <div className={styles.tableBody}>
                      {loadingRem && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                            Loading future appointments…
                          </div>
                        </div>
                      )}

                      {errorRem && !loadingRem && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                            {errorRem}
                          </div>
                        </div>
                      )}

                      {!loadingRem && !errorRem && remindersFiltered.length === 0 && (
                        <div className={styles.rowSoft}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1", opacity: 0.7 }}>
                            No data matches your filters.
                          </div>
                        </div>
                      )}


                      {!loadingRem && !errorRem && remindersFiltered.map((r) => {
                        const dateObj = r.date ? new Date(r.date) : null;
                        const sel = reminderSelected.has(r.id);

                        return (



                          <div
                        className={`${styles.rowSoft} ${styles.gridRem}`}
                        key={r.id}
                        onClick={() => openConsentBundle(r.raw?.id ?? r.id)}
                        style={{ cursor: "pointer" }}
                      >

                            <div className={styles.cell}>
                            <input
                                type="checkbox"
                                checked={sel}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleReminderRow(r.id)}
                              />

                            </div>

                            <div className={styles.cell}>
                              <div className={styles.dateBlock}>
                                <div className={styles.dateLine}><FiCalendar /> {dateObj ? fmtShort(dateObj) : "-"}</div>
                                <div className={styles.timeLine}><FiClock /> {r.time}</div>
                              </div>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.patient}>
                                <span className={styles.avatar}>
                                  {r.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                <span className={styles.patientName}>{r.patient}</span>
                              </div>
                            </div>

                              <div className={styles.cell}>
                              <div className={styles.patient}>
                                <span className={styles.patientName}>{r.date_of_birth}</span>
                              </div>
                            </div>

                            <div className={styles.cell}>{r.clinician}</div>
                            <div className={styles.cell}>
                              <span className={`${styles.pill} ${styles.pillDays}`}>
                                {r.days_until_appointment ?? r.daysUntilAppointment ?? 0} DAYS
                              </span>
                            </div>

                            <div className={styles.cell}>
                              {r.status?.toLowerCase() === "draft" ? (
                                <span className={`${styles.badge} ${styles.badgeGray}`}>DRAFT</span>
                              ) : r.status?.toLowerCase().includes("sign") ? (
                                <span className={`${styles.badge} ${styles.badgeYellow}`}>SENT FOR SIGNING</span>
                              ) : (
                                <span className={`${styles.badge} ${styles.badgeNotSigned}`}>{r.status ?? "UNKNOWN"}</span>
                              )}
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.iconWithNum}><FiMail /><span>{r.reminders ?? 0}</span></div>
                            </div>

                            <div className={styles.cell}>
                              {r.lastReminder ? fmtShort(new Date(r.lastReminder)) : "Never"}
                            </div>

                            <div className={`${styles.cell} ${styles.actions}`} style={{ display: "flex", gap: 1, alignItems: "center" }}>
                              

                            {canShowQr(r.status) && (
                                <button
                                  className={`${styles.btn} ${styles.btnLight}`}
                                  title="QR Code"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openQr(r);
                                  }}
                                >
                                  <FiGrid />
                                </button>
                              )}

                            {/* 👁 Quick View */}
                            <button
                              className={`${styles.btn} ${styles.btnLight}`}
                              title="Quick view"
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuickView(r.raw?.id ?? r.id);
                              }}
                            >
                              <FiEye /> 
                            </button>

                               <button
                              className={`${styles.btn} ${styles.btnLight}`}
                              title="Download"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadSingleAll(r.raw?.id ?? r.id);
                              }}
                            >
                              {downloadingIds.has(r.id) ? (
                                <span className={styles.spinner}></span>
                              ) : (
                                <>
                                  <FiDownload /> 
                                </>
                              )}
                            </button>

                            {/* Archive */}
                            <button
                            className={`${styles.btn} ${styles.btnLight}`}
                            title="Archive"
                            onClick={(e) => {
                              e.stopPropagation();
                              askArchive(r.id, r.patient, "all");
                            }}
                          >
                            <FiArchive />
                          </button>

                              <Tooltip title="Send reminder">
                                <span>
                                  <button
                                    className={`${styles.btn} ${styles.btnOutlineBlue}`}
                                    disabled={r.status?.toLowerCase() === "draft"}   
                                    onClick={(e) => {
                                      e.stopPropagation(); 
                                      const id = Number(r.raw?.id ?? r.id);
                                      setPendingReminderIds([id]);
                                      setShowConfirmModal(true);
                                    }}
                                  >
                                    <FiSend /> 
                                  </button>
                                </span>
                              </Tooltip>

                              
                            </div>
                          </div>
                        );
                      })}

                    </div>
                  </div>

                   {reminderSelected.size > 0 && (
                      <button
                        className={styles.bulkBtnTop}
                        disabled={bulkSendDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          const ids = getSelectedReminderBundleIds();
                          if (!ids.length) {
                            toast.error("No valid consent bundle IDs found.");
                            return;
                          }
                          setPendingReminderIds(ids);
                          setShowConfirmModal(true);
                        }}
                      >
                        <FiSend style={{ marginRight: 6 }} />
                        Bulk send ({reminderSelected.size})
                      </button>
                    )}

                </>
              )}
              {/* ---- Ready for upload ---- */}
              {activeTab === "upload" && (
                <>
                  <h4 className={styles.sectionTitle}>Ready for upload</h4>

                  <div className={styles.table}>
                    <div className={`${styles.gridUpload} ${styles.tableHead}`}>
                      <div>
                      
                      <Tooltip title="Select all items">
                        <div className={styles.checkboxRow}>
                          <input
                            type="checkbox"
                            checked={uploadFiltered.length > 0 && uploadFiltered.every(r => uploadSelected.has(r.id))}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleUploadAll();
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className={styles.checkboxLabel}></span>
                        </div>
                      </Tooltip>
                      </div>
                      <div>Patient name</div>
                      <div>Appointment date</div>
                      <div>Clinician</div>
                      <div>Status</div>
                      <div>Email</div>
                      <div>SMS</div>
                      <div>Signed date</div>
                      <div>Actions</div>
                    </div>

                    <div className={styles.tableBody}>
                      {loadingUpload && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                            Loading ready-for-upload…
                          </div>
                        </div>
                      )}

                      {errorUpload && !loadingUpload && (
                        <div className={styles.row}>
                          <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                            {errorUpload}
                          </div>
                        </div>
                      )}

                      {!loadingUpload && !errorUpload && uploadFiltered.length === 0 && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1", opacity: 0.7 }}>
                          No data matches your filters.
                        </div>
                      </div>
                    )}


                      {!loadingUpload && !errorUpload && uploadFiltered.map((u) => {
                        const sel = uploadSelected.has(u.id);
                        const completed = u.raw?.uploaded_to_dentally_patient_record === true || String(u.status).toLowerCase().includes("completed");

                        return (
                          <div
                          className={`${styles.gridUpload} ${
                            completed ? styles.rowUploadCompleted : styles.rowUpload
                          }`}
                          key={u.id}
                          onClick={() => openConsentBundle(u.raw?.id ?? u.id)}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                            <div className={styles.cell}>
                              <input
                              className={styles.checkboxLarge}
                                type="checkbox"
                                checked={sel}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleUploadRow(u.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.patient}>
                                <span className={styles.avatar}>
                                  {u.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                <span className={styles.patientName}>{u.patient}</span>
                              </div>
                            </div>

                            <div className={styles.cell}>{fmtShort(new Date(u.appointmentDate))}</div>
                            <div className={styles.cell}>{u.clinician}</div>

                            <div className={styles.cell}>
                              {(() => {
                                const isArchived =
                                  u.isArchived === true ||
                                  u.raw?.is_archived === true ||
                                  u.raw?.archived === true ||
                                  !!u.raw?.archived_at;

                                if (isArchived) {
                                  return (
                                    <span className={`${styles.badge} ${styles.badgeYellow}`}>
                                      ARCHIVED
                                    </span>
                                  );
                                }

                                const uploaded = Boolean(u.raw?.uploaded_to_dentally_patient_record);
                                const cls = getUploadStatusBadgeClass(u.status, uploaded);

                                return (
                                  <span className={`${styles.badge} ${styles[cls]}`}>
                                    {uploaded ? "COMPLETED" : (u.status || "UNKNOWN")}
                                  </span>
                                );
                              })()}
                            </div>


                            <div className={styles.cell}>
                              <div className={styles.iconWithNum}><FiMail /><span>{u.email}</span></div>
                            </div>

                            <div className={styles.cell}>
                              <div className={styles.iconWithNum}><FiMessageCircle /><span>{u.sms}</span></div>
                            </div>

                            <div className={styles.cell}>
                              {u.signedDate ? fmtShort(new Date(u.signedDate)) : "Not signed"}
                            </div>

                            <div className={styles.cell} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                
                              {/* 👁 Quick View */}
                              <button
                                className={styles.iconBtn}
                                title="Quick view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openQuickView(u.raw?.id ?? u.id);
                                }}
                                style={{ border: "none", background: "transparent", padding: 3 }}
                              >
                                <FiEye />
                              </button>

                                <button
                                className={styles.iconBtn}
                                title="Download"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadSingleAll(u.raw?.id ?? u.id);
                                }}
                                style={{ border: "none", background: "transparent", padding: 3 }}
                              >
                                {downloadingIds.has(u.id) ? (
                                  <span className={styles.spinner}></span>
                                ) : (
                                  <FiDownload />
                                )}
                              </button>
                              <button
                                className={styles.iconBtn}
                                title="Archive"
                                onClick={() => askArchive(u.raw?.id ?? u.id, u.patient, "upload")}
                                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}
                              >
                                <FiArchive />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.uploadFooter}>
                    <div className={styles.uploadCount}> 
                      {uploadSelected.size} of {uploadFiltered.length} items selected
                    </div>
                  <div className={styles.uploadBtns}>
                      <button
                        className={styles.btnSecondary}
                        disabled={uploadSelected.size === 0}
                        onClick={doBulkArchiveUpload}
                      >
                        Archive selected
                      </button>
                      <button
                        className={styles.btnPrimary}
                        disabled={uploadSelected.size === 0}
                        onClick={doBulkDownload}
                      >
                        Download
                      </button>
                    </div>
                  </div>

                  <div className={styles.tableFooter}>
                    <div className={styles.pagerInfo}>
                      Page {pageUpload} • Per page
                      <select
                        value={perPageUpload}
                        onChange={(e) => { setPageUpload(1); setPerPageUpload(parseInt(e.target.value, 10)); }}
                        style={{ marginLeft: 8 }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span style={{ marginLeft: 12 }}>Total: {totalUpload}</span>
                    </div>
                    <div className={styles.pagerBtns}>
                      <button
                        className={styles.btn}
                        disabled={pageUpload <= 1 || loadingUpload}
                        onClick={() => setPageUpload(p => Math.max(1, p - 1))}
                      >
                        Prev
                      </button>
                      <button
                        className={styles.btn}
                        disabled={loadingUpload || (pageUpload * perPageUpload >= totalUpload)}
                        onClick={() => setPageUpload(p => p + 1)}
                        style={{ marginLeft: 8 }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}  
          </div>
 
          {/* bulk confirmation modal */}
          <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
            <Box
              sx={{
                background: "#fff",
                padding: 3,
                width: 400,
                mx: "auto",
                mt: "20vh",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <h3>Send Reminder?</h3>

              <p style={{ margin: "15px 0" }}>
                {pendingReminderIds.length === 1
                  ? "Are you sure you want to send a reminder for this consent?"
                  : `Are you sure you want to send reminders to ${pendingReminderIds.length} selected consents?`}
              </p>

              <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    sendReminders(pendingReminderIds);   // your API call
                    setShowConfirmModal(false);
                  }}
                >
                  Yes, Send
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </Box>      
          </Modal>

        </section>

        <footer className={styles.footerStripe} />
    </div>


{quickViewOpen && (
  <div className={styles.quickViewBackdrop} onClick={() => setQuickViewOpen(false)}>
    <div
      className={styles.quickViewModal}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className={styles.quickViewTitle}>
        Interaction Log – #{quickViewId}
      </h3>

      {/* Loading */}
      {quickViewLoading && (
        <div className={styles.quickViewLoading}>Loading…</div>
      )}

      {/* Error */}
      {quickViewError && (
        <div className={styles.quickViewError}>{quickViewError}</div>
      )}

      {/* Content */}
      {!quickViewLoading && !quickViewError && (
        <div className={styles.quickViewTimeline}>

          {Object.entries(interactionLog).map(([date, events]) => (
            <div key={date} className={styles.timelineDay}>
              
              <div className={styles.timelineDate}>{date}</div>

              <div className={styles.timelineItems}>
                {events.map((ev, idx) => (
                  <div key={idx} className={styles.timelineItem}>
                    <div className={styles.timelineDot}></div>

                    <div className={styles.timelineContent}>
                      <div className={styles.timelineEvent}>{ev.event}</div>

                      {ev.description?.length > 0 && (
                        <ul className={styles.timelineDesc}>
                          {ev.description.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          ))}

          {Object.keys(interactionLog).length === 0 && (
            <p className={styles.timelineNoData}>No interactions found.</p>
          )}

        </div>
      )}

      <button
        className={styles.quickViewClose}
        onClick={() => setQuickViewOpen(false)}
      >
        Close
      </button>
    </div>
  </div>
)}

{isBulkDownloading && (
  <div className={styles.fullscreenLoader}>
    <div className={styles.loaderContent}>
      <Lottie
        animationData={bulkDownloadAnim}
        loop
        style={{ width: 260, height: 260 }}
      />
      <div className={styles.loaderText}>Preparing your download...</div>
    </div>
  </div>
)}

{bulkDownloading && (
  <div className={styles.bulkOverlay}>
    <div className={styles.bulkOverlayContent}>
      <Lottie 
        animationData={bulkDownloadAnim} 
        loop
        style={{ width: 220, height: 220 }}
      />
      <p className={styles.bulkText}>Preparing your download...</p>
    </div>
  </div>
)}
      {/* {showTutorial && (
        <TutorialOverlay steps={steps} onClose={() => setShowTutorial(false)} />
      )} */}


    {notify.show && (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "#fff",
          padding: "24px 32px",
          borderRadius: 12,
          boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
          zIndex: 99999,
          textAlign: "center",
          minWidth: 180,
        }}
      >
        {notify.type === "archive" && (
          <div style={{ fontSize: 48, marginBottom: 8, animation: "binAnim 1.5s ease" }}>
            🗑️
          </div>
        )}

        {notify.type === "download" && (
          <div style={{ fontSize: 40, marginBottom: 8, animation: "spin 1s linear infinite" }}>
            ⬇️
          </div>
        )}

        {notify.type === "success" && (
          <div style={{ fontSize: 40, marginBottom: 8, color: "green" }}>
            ✅
          </div>
        )}

        <div style={{ fontWeight: 600 }}>{notify.message}</div>
      </div>
    )}

      {/* QR Modal */}
    <QrModal open={qrOpen} appt={selectedAppt} onClose={() => setQrOpen(false)} />

      {confirmArchive.open && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, padding: 24, width: "min(420px, 92vw)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Archive consent bundle</h3>
            <p style={{ marginTop: 0, opacity: 0.8 }}>
              Are you sure you want to archive{confirmArchive.name ? ` “${confirmArchive.name}”` : ""}?
            </p>

            <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "flex-end" }}>
              <button
                className={styles.btn}
                onClick={() => setConfirmArchive({ open: false, source: null, loading: false })}
                disabled={confirmArchive.loading}
              >
                Cancel
              </button>
              <button
                className={`${styles.btn} ${styles.btnOutlineBlue}`}
                onClick={doArchive}
                disabled={confirmArchive.loading}
              >
                {confirmArchive.loading ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
